import { useState } from 'react';
import { EmptyState, InlineError, Skeleton } from '../../components/ui';
import {
  batchShapeLabel,
  laneSummaryLine,
  outcomeSegments,
  replyRateBasis,
  replyRateLabel,
  unbatchedNote,
  useRoundBatches,
  useRoundLanes,
} from './roundLanes';
import type { RoundBatch, RoundLane } from './roundLanes';
import './rounds.css';

/**
 * The Rounds tab: a lane at a time, and the batches that ran in it.
 *
 * ── What was wrong ─────────────────────────────────────────────────────────
 *
 * Kyu, 11 September: "When I click rounds, the rounds view doesn't explain to
 * me what's going on. The batches per round, how each batch performed, how all
 * round 1s perform."
 *
 * What he was clicking was the Rounds TOGGLE on the leads table, which groups
 * the current list by round number. That is a work queue and it stays exactly
 * as it is. It answers "who do I chase today". It cannot answer "did Round 1
 * work", because a queue only contains people who are still in one.
 *
 * This is the other question, and it needed the two views in 20270138000100
 * before it could be asked at all.
 *
 * ── Lane first, because that is the comparison ─────────────────────────────
 *
 * The top-level control is the lane, not the date. Kyu: "I'm thinking of the
 * rounds mostly like their own realms or lanes in themselves." Round 1 against
 * Round 2 is the question the page exists for; October against November inside
 * Round 1 is the second question, and it is one level down.
 *
 * ── Refusing to state a reply rate ─────────────────────────────────────────
 *
 * A lane or batch where nothing has ended shows "Not yet", not "0%". The view
 * returns null on purpose and this passes it through. See roundLanes.ts.
 */
export function RoundsView() {
  const lanesQuery = useRoundLanes();
  const batchesQuery = useRoundBatches();
  const [selected, setSelected] = useState<number | null>(null);

  if (lanesQuery.isPending || batchesQuery.isPending) {
    return <Skeleton lines={5} label="Loading rounds" />;
  }
  if (lanesQuery.isError) {
    return <InlineError>{lanesQuery.error.message}</InlineError>;
  }
  if (batchesQuery.isError) {
    return <InlineError>{batchesQuery.error.message}</InlineError>;
  }

  const lanes = lanesQuery.data;
  const batches = batchesQuery.data;

  if (lanes.length === 0) {
    return (
      <EmptyState
        title="No rounds have run yet"
        description="Start a follow-up round from the Leads table and it will appear here as Round 1."
      />
    );
  }

  // Default to the first lane rather than to none. A page that opens on an
  // empty selection makes the reader do a click to see anything at all.
  const lane = lanes.find((one) => one.number === selected) ?? lanes[0];
  if (lane === undefined) {
    return null;
  }
  const laneBatches = batches.filter((batch) => batch.number === lane.number);
  const note = unbatchedNote(lane);

  return (
    <div className="tracker-rounds">
      <div className="tracker-rounds-lanes" role="tablist" aria-label="Round lanes">
        {lanes.map((one) => (
          <button
            key={one.number}
            type="button"
            role="tab"
            aria-selected={one.number === lane.number}
            className={`tracker-rounds-lane${
              one.number === lane.number ? ' tracker-rounds-lane--active' : ''
            }`}
            onClick={() => {
              setSelected(one.number);
            }}
          >
            <b>Round {one.number}</b>
            <span>{laneSummaryLine(one)}</span>
          </button>
        ))}
      </div>

      <LaneSummary lane={lane} />

      <div className="tracker-dashboard-card">
        <div className="tracker-rounds-head">
          <p className="tracker-dashboard-card-title">Batches in this lane</p>
          <p className="tracker-rounds-note">
            {laneBatches.length === 0
              ? 'None yet'
              : `${String(laneBatches.length)} batch${laneBatches.length === 1 ? '' : 'es'}, newest first`}
          </p>
        </div>

        {laneBatches.length === 0 ? (
          <EmptyState
            title="No batches in this lane yet"
            description="A batch is created the moment you start a round for a group of leads at once."
          />
        ) : (
          <ul className="tracker-rounds-batches">
            {laneBatches.map((batch) => (
              <BatchRow key={batch.batchId} batch={batch} />
            ))}
          </ul>
        )}

        {note === null ? null : <p className="tracker-rounds-footnote">{note}</p>}
      </div>
    </div>
  );
}

/** The lane's own numbers, above the batches that make them up. */
function LaneSummary({ lane }: { lane: RoundLane }) {
  return (
    <div className="tracker-kpi-grid">
      <div className="tracker-kpi-card">
        <p className="tracker-kpi-label">Reply rate</p>
        <p className="tracker-kpi-value">{replyRateLabel(lane.replyRate, lane.endedRounds)}</p>
        <p className="tracker-kpi-sub">{replyRateBasis(lane.replied, lane.endedRounds)}</p>
      </div>
      <div className="tracker-kpi-card">
        <p className="tracker-kpi-label">Leads</p>
        <p className="tracker-kpi-value">{lane.leads}</p>
        <p className="tracker-kpi-sub">{laneSummaryLine(lane)}</p>
      </div>
      <div className="tracker-kpi-card">
        <p className="tracker-kpi-label">Batches</p>
        <p className="tracker-kpi-value">{lane.batches}</p>
        <p className="tracker-kpi-sub">
          {lane.unbatchedLeads === 0
            ? 'all leads in a batch'
            : `${String(lane.unbatchedLeads)} started one at a time`}
        </p>
      </div>
      <div className="tracker-kpi-card">
        <p className="tracker-kpi-label">Ran the full course</p>
        <p className="tracker-kpi-value">{lane.countMet}</p>
        <p className="tracker-kpi-sub">
          {lane.countMet === 0 ? 'never hit the target' : 'every follow-up sent'}
        </p>
      </div>
      <div className="tracker-kpi-card">
        <p className="tracker-kpi-label">Follow-ups per lead</p>
        <p className="tracker-kpi-value">{lane.touchesPerLead ?? '—'}</p>
        <p className="tracker-kpi-sub">
          {lane.usualTarget === null
            ? 'actually sent'
            : `against a ${String(lane.usualTarget)} target`}
        </p>
      </div>
    </div>
  );
}

/** One batch, with how it went. */
function BatchRow({ batch }: { batch: RoundBatch }) {
  const segments = outcomeSegments(batch);
  return (
    <li className={`tracker-rounds-batch${batch.isRunning ? ' tracker-rounds-batch--live' : ''}`}>
      <div className="tracker-rounds-batch-name">
        <b>{batch.name}</b>
        <span>
          {batchShapeLabel(batch)} · started {batch.startsOn}
        </span>
      </div>

      <div className="tracker-rounds-batch-stats">
        <div className="tracker-rounds-stat">
          <b>{replyRateLabel(batch.replyRate, batch.endedRounds)}</b>
          <span>replied</span>
        </div>
        <div className="tracker-rounds-stat">
          <b>{batch.leads}</b>
          <span>leads</span>
        </div>
        <div className="tracker-rounds-stat">
          <b>{batch.touchesPerLead ?? '—'}</b>
          <span>sent each</span>
        </div>

        {/* flex-grow by count, so no percentage is ever rounded. */}
        <div
          className="tracker-rounds-bar"
          role="img"
          aria-label={segments.map((one) => `${one.label} ${String(one.count)}`).join(', ')}
        >
          {segments.map((one) => (
            <i
              key={one.key}
              className={`tracker-rounds-bar-part tracker-rounds-bar-part--${one.key}`}
              style={{ flexGrow: one.count }}
            />
          ))}
        </div>
      </div>
    </li>
  );
}
